import { Col, Divider, Modal, Row, Space, Tooltip } from "antd";
import { EditOutlined, InfoCircleFilled } from "@ant-design/icons";
import { ColumnsType } from "antd/lib/table";
import { useEffect, useState } from "react";
import { useAsyncAxios, utilAxiosWithAuth } from "../utils/customAxios";
import { AlertType, useAlert } from "../provider/AlertProvider";

import * as Types from "../commons/typeDTO";
import * as Common from "../commons/common";
import * as Request from "../commons/request";
import * as String from "../commons/string";

import DataTable from "./DataTable";
import moment from "moment";

function MyCoupon({ user }: { user?: Types.UserDto }) {
    const alert = useAlert();
    const [userCoupons, setUserCoupons] = useState<Array<Types.UserCouponDto>>();

    useEffect(() => {
        if (!user) return;

        requestGetUserCoupons(user?.email);
    }, [user]);

    //get user coupons
    const requestAxiosGetUserCoupons = async (email: string) => {
        const response = await utilAxiosWithAuth().get(Request.USER_COUPONS_URL, {
            params: { email: email },
        });
        return response.data;
    };

    const {
        loading: loadingGetUserCoupons,
        error: errorGetUserCoupons,
        data: resultGetUserCoupons,
        execute: requestGetUserCoupons,
    } = useAsyncAxios(requestAxiosGetUserCoupons);

    useEffect(() => {
        if (!resultGetUserCoupons) return;

        console.log("couponList", resultGetUserCoupons.userCoupons);
        setUserCoupons(
            resultGetUserCoupons.userCoupons?.filter(
                (item: Types.UserCouponDto) => (item.effectiveDate ?? item.coupon.effectiveDate) <= moment().format(Common.FORMAT_DATE_TIME)
            )
        );
    }, [resultGetUserCoupons]);

    useEffect(() => {
        if (errorGetUserCoupons === null) return;

        alert.setAlert(errorGetUserCoupons.response.data.code, "Error", errorGetUserCoupons.response.data.message);
    }, [errorGetUserCoupons]);

    //update user coupon
    const requestAxiosUpdateUserCoupon = async (userCouponId: number, data: Types.UserCouponDto) => {
        const response = await utilAxiosWithAuth().put(Request.USER_COUPON_URL + userCouponId, data);
        return response.data;
    };

    const {
        loading: loadingUpdateUserCoupon,
        error: errorUpdateUserCoupon,
        data: resultUpdateUserCoupon,
        execute: requestUpdateUserCoupon,
    } = useAsyncAxios(requestAxiosUpdateUserCoupon);

    useEffect(() => {
        if (!resultUpdateUserCoupon) return;
        console.log("resultUpdateUserCoupon", resultUpdateUserCoupon.userCoupon);
        alert.setAlert(AlertType.SUCCESS, "쿠폰 사용 완료", "쿠폰 상태를 변경했습니다.");
        requestGetUserCoupons();
    }, [resultUpdateUserCoupon]);

    useEffect(() => {
        if (errorUpdateUserCoupon === null) return;

        alert.setAlert(errorUpdateUserCoupon.response.data.code, "Error", errorUpdateUserCoupon.response.data.message);
    }, [errorUpdateUserCoupon]);

    //cancel user coupon
    const requestAxiosCancelUserCoupon = async (userCouponId: number) => {
        const response = await utilAxiosWithAuth().put(Request.USER_COUPON_URL + userCouponId + "/cancel");
        return response.data;
    };

    const {
        loading: loadingCancelUserCoupon,
        error: errorCancelUserCoupon,
        data: resultCancelUserCoupon,
        execute: requestCancelUserCoupon,
    } = useAsyncAxios(requestAxiosCancelUserCoupon);

    useEffect(() => {
        if (!resultCancelUserCoupon) return;
        console.log("resultCancelUserCoupon", resultCancelUserCoupon.userCoupon);
        alert.setAlert(AlertType.SUCCESS, "쿠폰 사용 가능", "쿠폰 상태를 변경했습니다.");
        requestGetUserCoupons();
    }, [resultCancelUserCoupon]);

    useEffect(() => {
        if (errorCancelUserCoupon === null) return;

        alert.setAlert(errorCancelUserCoupon.response.data.code, "Error", errorCancelUserCoupon.response.data.message);
    }, [errorCancelUserCoupon]);

    const columns: ColumnsType<Types.UserCouponDto> = [
        {
            align: "center",
            title: "번호",
            dataIndex: "num",
            width: "6%",
            render: (item, record, index) => <>{userCoupons && userCoupons.indexOf(record) + 1}</>,
        },
        {
            align: "center",
            title: "발급일",
            dataIndex: "createdDate",
            render: (_, row) => row.createdDate?.substring(2, 16),
        },
        {
            align: "center",
            title: "쿠폰명",
            dataIndex: ["coupon", "couponName"],
        },
        {
            align: "center",
            title: "사용분류",
            dataIndex: ["coupon", "multiCoupon"],
            render: (_, row) => (row.coupon.multiCoupon ? "중복" : "단독"),
        },
        {
            align: "center",
            title: "쿠폰 사용 시작일",
            dataIndex: "effectiveDate",
            render: (_, row) => row.effectiveDate?.substring(0, 10) ?? row.coupon.effectiveDate?.substring(0, 10),
        },
        {
            align: "center",
            title: "쿠폰 사용 만료일",
            dataIndex: "expiredDate",
            render: (_, row) => row.expiredDate?.substring(0, 10) ?? row.coupon.expiredDate?.substring(0, 10),
        },
        {
            align: "center",
            title: "사용일",
            dataIndex: "usedDate",
            render: (_, row) => <>{row.usedDate || String.dash}</>,
        },
        {
            align: "center",
            title: "상태",
            dataIndex: "status",
            render: (_, row) =>
                (row.expiredDate ?? row.coupon.expiredDate) < moment().format(Common.FORMAT_DATE_TIME) ? (
                    "기간 만료"
                ) : (
                    <Space
                        style={{ cursor: "pointer" }}
                        size={2}
                        onClick={() => {
                            Modal.confirm({
                                title: "쿠폰 상태 변경",
                                content: (row.usedDate ? "사용 가능" : "사용 완료") + " 상태로 변경하시겠습니까?",
                                okText: "변경",
                                cancelText: "취소",
                                onOk() {
                                    !row.usedDate
                                        ? requestUpdateUserCoupon(row.userCouponId, { usedDate: moment().format(Common.FORMAT_DATE_TIME) })
                                        : requestCancelUserCoupon(row.userCouponId);
                                },
                                centered: true,
                            });
                        }}
                    >
                        {row.usedDate ? "사용 완료" : "사용 가능"}
                        {<EditOutlined />}
                    </Space>
                ),
        },
        {
            align: "center",
            title: " ",
            dataIndex: "guidanceText",
            render: (_, row) => (
                <Tooltip title={<div style={{ whiteSpace: "pre-line" }}>{row.coupon.guidanceText}</div>}>
                    <InfoCircleFilled />
                </Tooltip>
            ),
        },
    ];

    return (
        <div>
            <Col span={6}>
                <Divider orientation="left">
                    <span>쿠폰정보</span>
                </Divider>
            </Col>
            <Row>
                <DataTable hasScroll={false} columns={columns} rowKey="orderNo" dataSource={userCoupons} />
            </Row>
        </div>
    );
}

export default MyCoupon;
